import React, { useRef, useState, useEffect } from "react";
import { Col, Container, Row } from "reactstrap";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import axios from "axios";
import * as XLSX from "xlsx";

const ImportFromCSV = () => {
    const stepperRef = useRef(null);
    const fileUploadRef = useRef(null);
    const toast = useRef(null);
    const [mappingFields, setMappingFields] = useState([]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [csvColumnOptions, setCsvColumnOptions] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [duplicateCriteria, setDuplicateCriteria] = useState("email");
    const [importType, setImportType] = useState("add_as_a_resume");
    const [importResults, setImportResults] = useState({
        totalRecords: 0,
        newRecords: 0,
        duplicateRecords: 0,
        corruptRecords: 0,
        processedRecords: 0,
    });
    const [token, setToken] = useState("");

    useEffect(() => {
        const userInfo = JSON.parse(sessionStorage.getItem("atsuser-info"));
        if (userInfo) {
            setToken(userInfo.token);
        }
    }, []);

    const formatFieldName = (fieldName) => {
        return fieldName
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const userInfos = JSON.parse(sessionStorage.getItem("atsuser-info"));
    const intialcompanyId = userInfos.user_details.companies_data[0].company_id;
    const [companyId, setCompanyId] = useState(intialcompanyId);

    useEffect(() => {
        if (!companyId) return;

        const fetchData = async () => {
            try {
                const response = await axios.get(
                    `/api/v1/get-import-fields/?company_id=${companyId}`
                );

                const candidateFields = response.data.candidate.map((field) => ({
                    atsField: formatFieldName(field),
                    dbField: field,
                    defaultValue: "",
                    csvColumn: "",
                }));

                setMappingFields(candidateFields);
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to fetch import fields",
                    life: 3000,
                });
            }
        };

        fetchData();
    }, [companyId]);

    // New useEffect to automatically map fields when csvColumnOptions change
    useEffect(() => {
        if (csvColumnOptions.length > 0 && mappingFields.length > 0) {
            const updatedMappingFields = mappingFields.map((field) => {
                // Find a matching CSV column (case-insensitive comparison)
                const matchingOption = csvColumnOptions.find(
                    (option) =>
                        option.value.toLowerCase().trim() === field.atsField.toLowerCase().trim() ||
                        option.value.toLowerCase().trim() === field.dbField.toLowerCase().trim()
                );

                return {
                    ...field,
                    csvColumn: matchingOption ? matchingOption.value : field.csvColumn,
                };
            });

            // Update selected columns to include auto-mapped ones
            const newSelectedColumns = updatedMappingFields
                .filter((field) => field.csvColumn)
                .map((field) => field.csvColumn);

            setMappingFields(updatedMappingFields);
            setSelectedColumns([...new Set(newSelectedColumns)]); // Use Set to avoid duplicates
        }
    }, [csvColumnOptions]);

    const allowedExtensions = [".csv"];
    const forbiddenExtensions = [
        "js",
        "ts",
        "jsx",
        "tsx",
        "py",
        "java",
        "c",
        "cpp",
        "cs",
        "rb",
        "php",
        "sh",
        "html",
        "css",
        "json",
        "pdf",
        "docx",
        "jpeg",
        "png",
        "jpg",
    ];

    const validateFile = (file) => {
        const fileExtension = `.${file.name.split(".").pop().toLowerCase()}`;
        if (!allowedExtensions.includes(fileExtension)) {
            toast.current.show({
                severity: "error",
                summary: "Invalid File",
                detail: `Only CSV files (.csv) are allowed.`,
                life: 3000,
            });
            return false;
        }
        return true;
    };

    const onSelect = (event) => {
        const file = event.files[0];
        if (!file || !validateFile(file)) {
            setUploadedFile(null);
            setCsvColumnOptions([]);
            setSelectedColumns([]);
            fileUploadRef.current.clear();
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                const headers = jsonData[0].map((header) => ({
                    label: `${header}`,
                    value: `${header}`,
                }));
                setCsvColumnOptions(headers);
                setUploadedFile(file);
                toast.current.show({
                    severity: "success",
                    summary: "File Uploaded",
                    detail: `${file.name} successfully uploaded.`,
                    life: 3000,
                });
            } catch (error) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: `Failed to read ${file.name}.`,
                    life: 3000,
                });
                setUploadedFile(null);
                setCsvColumnOptions([]);
                setSelectedColumns([]);
                fileUploadRef.current.clear();
            }
        };
        reader.onerror = () => {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: `Failed to read ${file.name}.`,
                life: 3000,
            });
            setUploadedFile(null);
            setCsvColumnOptions([]);
            setSelectedColumns([]);
            fileUploadRef.current.clear();
        };
        reader.readAsArrayBuffer(file);
    };

    const onCSVColumnChange = (e, index) => {
        const updatedMappingFields = [...mappingFields];
        const previousValue = updatedMappingFields[index].csvColumn;
        updatedMappingFields[index].csvColumn = e.value;

        setSelectedColumns((prev) => {
            const newSelected = [...prev.filter((val) => val !== previousValue)];
            if (e.value) {
                newSelected.push(e.value);
            }
            return newSelected;
        });

        setMappingFields(updatedMappingFields);
    };

    const onATSFieldChange = (e, index) => {
        let updatedMappingFields = [...mappingFields];
        updatedMappingFields[index].atsField = e.target.value;
        setMappingFields(updatedMappingFields);
    };

    const onDefaultValueChange = (e, index) => {
        let updatedMappingFields = [...mappingFields];
        updatedMappingFields[index].defaultValue = e.target.value;
        setMappingFields(updatedMappingFields);
    };

    const onRemove = (event) => {
        setUploadedFile(null);
        setCsvColumnOptions([]);
        setSelectedColumns([]);
        toast.current.show({
            severity: "info",
            summary: "File Removed",
            detail: `${event.file.name} removed.`,
            life: 3000,
        });
    };

    const onClear = () => {
        setUploadedFile(null);
        setCsvColumnOptions([]);
        setSelectedColumns([]);
    };

    const getFilteredOptions = (currentValue) => {
        return csvColumnOptions.filter(
            (option) => !selectedColumns.includes(option.value) || option.value === currentValue
        );
    };

    const handleDuplicateCriteriaChange = (e) => {
        setDuplicateCriteria(e.target.value);
    };

    const handleImportTypeChange = (e) => {
        setImportType(e.target.value);
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result.split(",")[1];
                resolve(base64String);
            };
            reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
            reader.readAsDataURL(file);
        });
    };

    const handleImportSubmit = async () => {
        try {
            if (!uploadedFile) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: "No file uploaded.",
                    life: 3000,
                });
                return;
            }

            const base64File = await fileToBase64(uploadedFile);

            const fieldData = mappingFields.map((field) => ({
                db_field: field.dbField,
                excel_field: field.csvColumn ? field.csvColumn.split(" (")[0] : "",
                default_value: field.defaultValue,
            }));

            const payload = {
                duplicate_criteria: duplicateCriteria === "email" ? "email" : "phone_number",
                import_type: importType === "add_as_a_resume" ? "add as new" : importType === "overwrite_existing" ? "overwrite" : "do nothing",
                field_data: fieldData,
                base_64_file: base64File,
            };

            const response = await axios.post(`/api/v1/import-candidates/?company_id=${companyId}`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setImportResults({
                totalRecords: response.data.total_records || 0,
                newRecords: response.data.total_new_records || 0,
                duplicateRecords: response.data.total_duplicate_records || 0,
                corruptRecords: response.data.total_corrupt_records || 0,
                processedRecords: response.data.total_processed_records || 0,
            });

            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Data imported successfully.",
                life: 3000,
            });

            stepperRef.current.nextCallback();
        } catch (error) {
            console.error("Error importing data:", error);
            toast.current.show({
                severity: "warning",
                summary: "Warning",
                detail: "Failed to import data.",
                life: 3000,
            });
        }
    };

    return (
        <React.Fragment>
            <div className="page-content allact-tabs">
                <Container fluid={true}>
                    <div className="page-title-box actjobbread">
                        <Row>
                            <Col sm={12} md={12} lg={12}>
                                <h1 className="page-title">Import Candidates</h1>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12}>
                                <div>
                                    <Toast ref={toast} />
                                    <Stepper ref={stepperRef} style={{ flexBasis: "50rem" }}>
                                        <StepperPanel header="Choose">
                                            <div className="flex flex-column">
                                                <h6 className="page-title mb-3">Upload CSV</h6>
                                                <FileUpload
                                                    ref={fileUploadRef}
                                                    name="demo"
                                                    accept=".csv"
                                                    maxFileSize={10000000}
                                                    customUpload
                                                    auto
                                                    onSelect={onSelect}
                                                    onRemove={onRemove}
                                                    onClear={onClear}
                                                    emptyTemplate={<p className="m-0">Drag and drop a CSV file here to upload.</p>}
                                                />
                                            </div>
                                            <div className="flex pt-4 justify-content-end">
                                                <Button
                                                    label="Next"
                                                    icon="pi pi-arrow-right"
                                                    iconPos="right"
                                                    onClick={() => stepperRef.current.nextCallback()}
                                                    disabled={!uploadedFile}
                                                />
                                            </div>
                                        </StepperPanel>

                                        <StepperPanel header="Duplicate Check">
                                            <div className="flex flex-column">
                                                <Row>
                                                    <Col md={12}>
                                                        <h6 className="page-title mb-3">Candidate Duplicate Check</h6>
                                                        <div className="card p-3 dup-check">
                                                            <div className="mb-3">
                                                                <label className="mb-2 import-head">
                                                                    Please choose an action to take if a duplicate candidate is detected during the upload.
                                                                </label>
                                                                <div className="mt-2">
                                                                    <div>
                                                                        <input
                                                                            type="radio"
                                                                            id="add_as_a_resume"
                                                                            name="category"
                                                                            value="add_as_a_resume"
                                                                            checked={importType === "add_as_a_resume"}
                                                                            onChange={handleImportTypeChange}
                                                                        />
                                                                        <label
                                                                            htmlFor="add_as_a_resume"
                                                                            className="ms-2 mb-0 import-fw"
                                                                        >
                                                                            Add as a Candidate
                                                                        </label>
                                                                        <p className="txt">
                                                                            Select this option to create a new candidate in the system.
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <input
                                                                            type="radio"
                                                                            id="overwrite_existing"
                                                                            name="category"
                                                                            value="overwrite_existing"
                                                                            checked={importType === "overwrite_existing"}
                                                                            onChange={handleImportTypeChange}
                                                                        />
                                                                        <label
                                                                            htmlFor="overwrite_existing"
                                                                            className="ms-2 mb-0 import-fw"
                                                                        >
                                                                            Overwrite Existing
                                                                        </label>
                                                                        <p className="txt">
                                                                            Choose this option to replace the existing candidate with the new one, moving the old candidate to the documents section.
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <input
                                                                            type="radio"
                                                                            id="do_nothing"
                                                                            name="category"
                                                                            value="do_nothing"
                                                                            checked={importType === "do_nothing"}
                                                                            onChange={handleImportTypeChange}
                                                                        />
                                                                        <label
                                                                            htmlFor="do_nothing"
                                                                            className="ms-2 mb-0 import-fw"
                                                                        >
                                                                            Do Nothing
                                                                        </label>
                                                                        <p className="txt">
                                                                            Opting for this option means the system will take no action upon detecting a duplicate record.
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="import-fw">Criteria for Identifying Duplicates:</p>
                                                                        <select
                                                                            className="form-select w-50"
                                                                            value={duplicateCriteria}
                                                                            onChange={handleDuplicateCriteriaChange}
                                                                        >
                                                                            <option value="email">Email</option>
                                                                            <option value="phone_number">Phone Number</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                            <div className="flex pt-4 justify-content-between">
                                                <Button
                                                    label="Back"
                                                    severity="secondary"
                                                    icon="pi pi-arrow-left"
                                                    onClick={() => stepperRef.current.prevCallback()}
                                                />
                                                <Button
                                                    label="Next"
                                                    icon="pi pi-arrow-right"
                                                    iconPos="right"
                                                    onClick={() => stepperRef.current.nextCallback()}
                                                />
                                            </div>
                                        </StepperPanel>

                                        <StepperPanel header="Import from CSV">
                                            <div className="flex flex-column">
                                                <h6 className="page-title mb-3">Import from CSV</h6>
                                                <Row>
                                                    <Col lg={12}>
                                                    <div style={{ maxHeight: "400px", overflowY: "auto", marginBottom: "1rem" }}>
                                                        <DataTable
                                                            value={mappingFields}
                                                            stripedRows
                                                            tableStyle={{ minWidth: "50rem" }}
                                                        >
                                                            <Column
                                                                field="atsField"
                                                                header="ATS Fields"
                                                                style={{ width: "30%" }}
                                                                body={(rowData, { rowIndex }) => (
                                                                    <InputText
                                                                        value={rowData.atsField}
                                                                        onChange={(e) => onATSFieldChange(e, rowIndex)}
                                                                        disabled
                                                                    />
                                                                )}
                                                            ></Column>
                                                            <Column
                                                                field="csvColumn"
                                                                header="CSV Columns"
                                                                style={{ width: "30%" }}
                                                                body={(rowData, { rowIndex }) => (
                                                                    <Dropdown
                                                                        value={rowData.csvColumn}
                                                                        options={getFilteredOptions(rowData.csvColumn)}
                                                                        onChange={(e) => onCSVColumnChange(e, rowIndex)}
                                                                        placeholder="Select"
                                                                        className="bgclr"
                                                                    />
                                                                )}
                                                            ></Column>
                                                            <Column
                                                                field="defaultValue"
                                                                header="Default Values"
                                                                style={{ width: "30%" }}
                                                                body={(rowData, { rowIndex }) => (
                                                                    <InputText
                                                                        value={rowData.defaultValue}
                                                                        onChange={(e) => onDefaultValueChange(e, rowIndex)}
                                                                    />
                                                                )}
                                                            ></Column>
                                                        </DataTable>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                            <div className="flex pt-2 justify-content-between">
                                                <Button
                                                    label="Back"
                                                    severity="secondary"
                                                    icon="pi pi-arrow-left"
                                                    onClick={() => stepperRef.current.prevCallback()}
                                                />
                                                <Button
                                                    label="Next"
                                                    icon="pi pi-arrow-right"
                                                    iconPos="right"
                                                    onClick={handleImportSubmit}
                                                />
                                            </div>
                                        </StepperPanel>

                                        <StepperPanel header="Finish">
                                            <div className="flex flex-column">
                                                <Row>
                                                    <Col md={12}>
                                                        <h6 className="page-title mb-3">Finish</h6>
                                                        <table className="table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Total Records</th>
                                                                    <th>Count</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td>Total New Records Added</td>
                                                                    <td>{importResults.newRecords}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Total Duplicate Records Found</td>
                                                                    <td>{importResults.duplicateRecords}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Total Corrupt Records Found</td>
                                                                    <td>{importResults.corruptRecords}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Total Processed Records</td>
                                                                    <td>{importResults.processedRecords}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </Col>
                                                </Row>
                                            </div>
                                            <div className="flex pt-4 justify-content-start">
                                                <Button
                                                    label="Back"
                                                    severity="secondary"
                                                    icon="pi pi-arrow-left"
                                                    onClick={() => stepperRef.current.prevCallback()}
                                                />
                                            </div>
                                        </StepperPanel>
                                    </Stepper>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ImportFromCSV;